class ProductBundleBox extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized === 'true') return;

    this.dataset.initialized = 'true';
    this.button = this.querySelector('[data-bundle-add]');
    this.partnerVariant = this.querySelector('[data-bundle-partner-variant]');
    this.variantButtons = [...this.querySelectorAll('[data-bundle-variant-button]')];
    this.selectedTitle = this.querySelector('[data-bundle-selected-title]');
    this.originalTotal = this.querySelector('[data-bundle-original-total]');
    this.discountTotal = this.querySelector('[data-bundle-discount-total]');
    this.savings = this.querySelector('[data-bundle-savings]');
    this.status = this.querySelector('[data-bundle-status]');

    this.variantButtons.forEach((variantButton) => {
      variantButton.addEventListener('click', () => this.selectVariant(variantButton));
    });

    this.button?.addEventListener('click', () => this.addBundle());

    const trackView = () => this.trackViewOnce();
    if (document.readyState === 'complete') trackView();
    else document.addEventListener('DOMContentLoaded', trackView, { once: true });
  }

  trackViewOnce() {
    window.DevidBundleViewedKeys = window.DevidBundleViewedKeys || new Set();
    const key = `${window.location.pathname}|${this.dataset.source || 'pdp'}|${this.dataset.discountScope || ''}`;
    if (window.DevidBundleViewedKeys.has(key)) return;
    window.DevidBundleViewedKeys.add(key);

    this.analytics('bundle_viewed', {
      source: this.dataset.source || 'pdp',
      discount_scope: this.dataset.discountScope || '',
      current_variant_id: Number(this.dataset.currentVariantId) || 0,
      partner_variant_id: Number(this.partnerVariant?.value) || 0
    });
  }

  analytics(name, detail = {}) {
    if (window.DevidCommerceAnalytics && typeof window.DevidCommerceAnalytics.publish === 'function') {
      window.DevidCommerceAnalytics.publish(name, detail);
    }
  }

  rememberBundleAttribution() {
    if (window.DevidCommerceAnalytics && typeof window.DevidCommerceAnalytics.rememberAttribution === 'function') {
      window.DevidCommerceAnalytics.rememberAttribution('bundle', {
        handle: this.dataset.source || ''
      });
    }
  }

  selectVariant(variantButton) {
    const variantId = variantButton.dataset.variantId;
    if (!variantId || !this.partnerVariant) return;

    this.partnerVariant.value = variantId;

    this.variantButtons.forEach((button) => {
      const isSelected = button === variantButton;
      button.classList.toggle('is-selected', isSelected);
      button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    });

    if (this.selectedTitle) this.selectedTitle.textContent = variantButton.dataset.variantTitle || '';
    if (this.originalTotal) this.originalTotal.textContent = variantButton.dataset.originalTotal || '';
    if (this.discountTotal) this.discountTotal.textContent = variantButton.dataset.discountTotal || '';
    if (this.savings) this.savings.textContent = variantButton.dataset.savings || '';

    this.clearError();
    this.analytics('bundle_variant_selected', {
      source: this.dataset.source || 'pdp',
      discount_scope: this.dataset.discountScope || '',
      current_variant_id: Number(this.dataset.currentVariantId) || 0,
      partner_variant_id: Number(variantId) || 0,
      partner_variant_title: variantButton.dataset.variantTitle || ''
    });
  }

  createBundleId() {
    if (window.crypto?.randomUUID) {
      return `bundle-${window.crypto.randomUUID()}`;
    }

    return `bundle-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  setLoading(isLoading) {
    if (!this.button) return;

    this.button.disabled = isLoading;
    this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    this.button.textContent = isLoading ? this.dataset.loadingLabel : this.dataset.defaultLabel;
  }

  showError(message) {
    if (!this.status) return;
    this.status.textContent = message || this.dataset.errorMessage;
  }

  clearError() {
    if (this.status) this.status.textContent = '';
  }

  async addBundle() {
    if (this.button?.getAttribute('aria-busy') === 'true') return;

    const currentVariantId = Number(this.dataset.currentVariantId);
    const partnerVariantId = Number(this.partnerVariant?.value);

    if (!currentVariantId || !partnerVariantId) {
      this.showError(this.dataset.unavailableMessage);
      this.analytics('bundle_add_failed', {
        source: this.dataset.source || 'pdp',
        reason: 'missing_variant'
      });
      return;
    }

    this.clearError();
    this.setLoading(true);
    this.analytics('bundle_add_started', {
      source: this.dataset.source || 'pdp',
      discount_scope: this.dataset.discountScope || '',
      current_variant_id: currentVariantId,
      partner_variant_id: partnerVariantId
    });
    document.documentElement.dispatchEvent(new CustomEvent('theme:loading:start', { bubbles: true }));

    try {
      const bundleId = this.createBundleId();
      const sectionsToBundle = [];

      document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      }));

      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: [
            {
              id: currentVariantId,
              quantity: 1,
              properties: {
                _bundle_id: bundleId,
                _bundle_role: 'primary',
                _bundle_source: this.dataset.source || 'pdp'
              }
            },
            {
              id: partnerVariantId,
              quantity: 1,
              properties: {
                _bundle_id: bundleId,
                _bundle_role: 'partner',
                _bundle_source: this.dataset.source || 'pdp'
              }
            }
          ],
          sections: sectionsToBundle.join(','),
          sections_url: window.location.pathname
        })
      });

      let responseJson = {};
      try {
        responseJson = await response.json();
      } catch (error) {
        responseJson = {};
      }

      if (!response.ok) {
        throw new Error(responseJson.description || responseJson.message || this.dataset.errorMessage);
      }

      this.rememberBundleAttribution();
      this.analytics('bundle_added', {
        source: this.dataset.source || 'pdp',
        discount_scope: this.dataset.discountScope || '',
        current_variant_id: currentVariantId,
        partner_variant_id: partnerVariantId,
        item_count: 2
      });

      if (window.themeVariables?.settings?.cartType === 'page' || window.themeVariables?.settings?.pageType === 'cart') {
        window.location.href = `${Shopify.routes.root}cart`;
        return;
      }

      const cartResponse = await fetch(`${Shopify.routes.root}cart.js`, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      const cart = await cartResponse.json();
      cart.sections = responseJson.sections || {};

      const productForm = this.closest('product-rerender')?.querySelector('form[action*="/cart/add"]')
        || document.querySelector('form[action*="/cart/add"]');
      const items = Array.isArray(responseJson.items) ? responseJson.items : [];

      productForm?.dispatchEvent(new CustomEvent('variant:add', {
        bubbles: true,
        detail: {
          items,
          cart,
          onSuccessDo: 'force_open_drawer'
        }
      }));

      document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
        bubbles: true,
        detail: {
          baseEvent: 'variant:add',
          onSuccessDo: 'force_open_drawer',
          cart
        }
      }));
    } catch (error) {
      this.showError(error.message || this.dataset.errorMessage);
      this.analytics('bundle_add_failed', {
        source: this.dataset.source || 'pdp',
        discount_scope: this.dataset.discountScope || '',
        current_variant_id: currentVariantId,
        partner_variant_id: partnerVariantId,
        reason: 'request_failed'
      });
      document.dispatchEvent(new CustomEvent('cart:refresh'));
    } finally {
      this.setLoading(false);
      document.documentElement.dispatchEvent(new CustomEvent('theme:loading:end', { bubbles: true }));
    }
  }
}

if (!window.customElements.get('product-bundle-box')) {
  window.customElements.define('product-bundle-box', ProductBundleBox);
}